/**
 * My Orders state — the Orders / Bookings tab (``?tab=``), both lists, booking cancel, the booking pay-balance flow
 * and the booking review flow. Moved verbatim out of OrderHistoryPage so the classic view and the Blinkit view share
 * one implementation.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, inr } from "../../../lib/api";

export function useOrdersController() {
    const [params, setParams] = useSearchParams();
    const tab = params.get("tab") === "bookings" ? "bookings" : "orders";
    const setTab = (t) => setParams(t === "orders" ? {} : { tab: t });

    const [orders, setOrders] = useState(null);
    const [bookings, setBookings] = useState(null);

    useEffect(() => {
        if (tab === "orders" && orders === null) {
            api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
        }
        if (tab === "bookings" && bookings === null) {
            api.get("/customer/bookings").then(({ data }) => setBookings(data || [])).catch(() => setBookings([]));
        }
    }, [tab, orders, bookings]);

    const counts = useMemo(() => ({
        orders: orders?.length ?? null,
        bookings: bookings?.length ?? null,
    }), [orders, bookings]);

    const cancelBooking = async (b) => {
        if (!window.confirm(`Cancel booking for "${b.product_name}"?`)) return;
        try {
            await api.post(`/customer/bookings/${b.id}/cancel`);
            toast.success("Booking cancelled");
            setBookings(null);   // force reload
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    // iter133 — pay-remaining flow. Prompts for an amount (defaults to
    // the full outstanding balance) then posts to the top-up endpoint.
    // Partial + full amounts both work; server validates against the
    // outstanding balance and blocks over-payment.
    const [paying, setPaying] = useState(null); // { booking, amount }
    const openPay = (b) => setPaying({ booking: b, amount: String((b.amount_total || 0) - (b.advance_paid || 0)) });
    // Accepts an optional amount override so "Pay full" can submit
    // immediately with the remaining balance instead of relying on
    // ``paying.amount`` state, which setState updates asynchronously —
    // previously "Pay full" only pre-filled the input and never actually
    // paid anything.
    const submitPay = async (amountOverride) => {
        if (!paying) return;
        const amt = parseFloat(amountOverride ?? paying.amount);
        if (isNaN(amt) || amt <= 0) { toast.error("Enter a positive amount"); return; }
        try {
            const { data } = await api.post(
                `/customer/bookings/${paying.booking.id}/pay-balance`,
                { amount: amt },
            );
            toast.success(
                data.payment.fully_paid
                    ? `Paid ${inr(data.payment.amount)} — booking is now fully paid`
                    : `Paid ${inr(data.payment.amount)} · ${inr(data.payment.remaining_after)} remaining`,
            );
            setPaying(null);
            setBookings(null);  // force reload
        } catch (e) { toast.error(e?.response?.data?.detail || "Payment failed"); }
    };

    // Booking reviews — mirrors the order review flow (OrderTrackPage.jsx),
    // just without the rider/multi-item bits bookings don't have. Like the
    // orders tab's "Rate & review" hint, this doesn't pre-check whether a
    // review already exists before showing the hint — opening the modal
    // fetches it and pre-fills so a re-submit reads as an edit.
    const [reviewing, setReviewing] = useState(null); // { booking, rating, comment, loading }
    const openReview = async (b) => {
        setReviewing({ booking: b, rating: 0, comment: "", loading: true });
        try {
            const { data } = await api.get(`/customer/bookings/${b.id}/review`);
            const r = data?.review;
            setReviewing({ booking: b, rating: r?.overall_rating || 0, comment: r?.overall_comment || "", loading: false });
        } catch {
            setReviewing({ booking: b, rating: 0, comment: "", loading: false });
        }
    };
    const submitReview = async () => {
        if (!reviewing || reviewing.rating < 1) { toast.error("Pick a star rating"); return; }
        try {
            await api.post(`/customer/bookings/${reviewing.booking.id}/review`, {
                overall_rating: reviewing.rating,
                overall_comment: reviewing.comment || undefined,
            });
            toast.success("Thanks for your review!");
            setReviewing(null);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to submit review"); }
    };

    return {
        tab, setTab, orders, setOrders, bookings, setBookings, counts, cancelBooking, paying, setPaying, openPay,
        submitPay, reviewing, setReviewing, openReview, submitReview,
    };
}
